import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Check, Crown, Sparkle, ArrowRight, Lock } from "@phosphor-icons/react";
import Modal from "@/components/Modal";
import Receipts from "@/components/billing/Receipts";
import { SubscriptionNotice, ConfirmPlanModal } from "@/components/billing/PlanActions";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, fmtDate } from "@/i18n/I18nContext";
import { PLANS } from "@/marketing/data";
import { toast } from "sonner";

const STATUS_COLORS = { trialing: "#C9A227", active: "#7A8B5C", past_due: "#B86B5A", canceled: "#6B6A67" };

export default function Billing() {
  const { t, lang } = useI18n();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [onvoReady, setOnvoReady] = useState(false);
  const [onvoSub, setOnvoSub] = useState(null);
  const [action, setAction] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/billing/me");
    setInfo(data);
    setReloadKey((k) => k + 1);
  }, []);
  const reloadAll = useCallback(async () => { await load(); await refresh(); }, [load, refresh]);

  useEffect(() => { load(); }, [load]);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setPolling(false); toast.error("Payment status check timed out."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        toast.success(t.billing.success);
        await load();
        await refresh();
        return;
      }
      if (data.status === "expired") { setPolling(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch (e) { setTimeout(() => pollStatus(sessionId, attempt + 1), 2000); }
  }, [load, refresh, t]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setPolling(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { api.get("/onvo/config").then(({ data }) => setOnvoReady(data.configured)).catch(() => setOnvoReady(false)); }, []);

  const pollOnvo = useCallback(async (subId, attempt = 0) => {
    if (attempt >= 10) { setPolling(false); toast.error("Payment status check timed out."); return; }
    try {
      const { data } = await api.get(`/onvo/subscription/${subId}`);
      if (data.billing_status === "active" && data.status === "active") {
        setPolling(false);
        toast.success(t.billing.success);
        await load();
        await refresh();
        return;
      }
    } catch (e) { /* retry */ }
    setTimeout(() => pollOnvo(subId, attempt + 1), 3000);
  }, [load, refresh, t]);

  useEffect(() => {
    if (!onvoSub) return;
    if (!window.onvo) { toast.error(t.billing.sdkMissing); return; }
    const el = document.getElementById("onvo-container");
    if (el) el.innerHTML = "";
    window.onvo.pay({
      publicKey: onvoSub.publishable_key,
      subscriptionId: onvoSub.subscription_id,
      customerId: onvoSub.customer_id,
      paymentType: "subscription",
      locale: lang,
      onSuccess: () => { const id = onvoSub.subscription_id; setOnvoSub(null); setPolling(true); pollOnvo(id); },
      onError: () => toast.error(t.billing.payFailed),
    }).render("#onvo-container");
  }, [onvoSub, lang, t, pollOnvo]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      if (onvoReady) {
        const { data } = await api.post("/onvo/subscribe", { plan });
        setOnvoSub(data);
        setBusy("");
        return;
      }
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  if (!info) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" /></div>;

  const statusLabel = t.billing[info.billing_status] || info.billing_status;
  const localized = PLANS[lang] || PLANS.en;
  const plans = [localized.basic, localized.elite];

  return (
    <div className="fade-up space-y-7 max-w-4xl" data-testid="billing-page">
      <div>
        <p className="label-caps">{t.billing.overline}</p>
        <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.billing.title}</h1>
      </div>

      {polling && (
        <div className="bg-white rounded-xl border border-[#E5E2DC] p-4 flex items-center gap-3 text-sm">
          <div className="h-5 w-5 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" /> {t.billing.processing}
        </div>
      )}

      {/* Current plan */}
      <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-[#2C3E35] flex items-center justify-center">
              {info.effective_plan === "elite" ? <Crown size={24} weight="duotone" color="#D4AF37" /> : <CreditCard size={24} weight="duotone" color="#FDFBF9" />}
            </div>
            <div>
              <p className="label-caps">{t.billing.currentPlan}</p>
              <div className="font-heading text-2xl font-semibold capitalize">{info.plan}</div>
            </div>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: `${STATUS_COLORS[info.billing_status]}20`, color: STATUS_COLORS[info.billing_status] }}>{statusLabel}</span>
            {info.billing_status === "trialing" && info.trial_ends_at && (
              <div className="text-xs text-[#6B6A67] mt-2">{t.billing.trialEnds}: {fmtDate(info.trial_ends_at, lang)}</div>
            )}
            {info.billing_status === "active" && info.current_period_end && (
              <div className="text-xs text-[#6B6A67] mt-2">{info.cancel_at_period_end ? t.billing.ends : t.billing.renews}: {fmtDate(info.current_period_end, lang)}</div>
            )}
          </div>
        </div>
        <SubscriptionNotice info={info} onCancel={() => setAction({ type: "cancel" })} onResumed={reloadAll} />
      </div>

      {/* Plans */}
      <div>
        <h2 className="font-heading text-xl font-medium mb-4">{t.billing.choosePlan}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {plans.map((p) => {
            const managed = info.payment_provider === "onvo" && info.billing_status === "active";
            const isCurrent = info.billing_status === "active" && (managed ? (info.pending_plan || info.plan) : info.plan) === p.id;
            const onClick = managed ? () => setAction({ type: "change", plan: p.id }) : () => subscribe(p.id);
            const elite = p.highlight;
            return (
              <div key={p.id} data-testid={`billing-plan-${p.id}`} className={`rounded-2xl p-6 border ${elite ? "bg-[#2C3E35] text-[#FDFBF9] border-[#D4AF37]/40 shadow-[0_0_40px_rgba(212,175,55,0.12)]" : "bg-white border-[#E5E2DC]"}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                  {elite && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#2C3E35] font-bold uppercase tracking-wide flex items-center gap-1"><Sparkle size={11} weight="fill" /> {t.pricing.mostPopular}</span>}
                </div>
                <div className="mt-3 flex items-end gap-2">
                  <span className="font-heading text-4xl font-extrabold">{p.crc}</span>
                  <span className={`pb-1.5 text-sm ${elite ? "text-[#FDFBF9]/60" : "text-[#6B6A67]"}`}>{t.billing.perMonth}</span>
                </div>
                <p className={`text-sm ${elite ? "text-[#D4AF37]" : "text-[#B86B5A]"} font-medium`}>{p.usd} USD{t.billing.perMonth}</p>
                <ul className={`mt-4 space-y-2 text-sm ${elite ? "text-[#FDFBF9]/85" : "text-[#2C3E35]/85"}`}>
                  {(lang === "es" ? (elite ? ["Recepcionista y agenda con IA", "Generador de marketing con IA", "Personal y sucursales ilimitados", "Analítica avanzada"] : ["Reservas y calendario", "CRM y pagos", "Hasta 3 miembros del personal", "Promociones de marketing"]) : (elite ? ["AI Receptionist & Scheduler", "AI Marketing Generator", "Unlimited staff & locations", "Advanced analytics"] : ["Bookings & Calendar", "CRM & Payments", "Up to 3 staff", "Marketing promotions"])).map((f) => (
                    <li key={f} className="flex items-center gap-2"><Check size={15} weight="bold" className={elite ? "text-[#D4AF37]" : "text-[#2C3E35]"} /> {f}</li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${p.id}`}
                  disabled={isCurrent || busy}
                  onClick={onClick}
                  className={`mt-6 w-full py-3 rounded-full font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 ${elite ? "bg-[#D4AF37] text-[#2C3E35] hover:bg-[#e0bd4a]" : "bg-[#2C3E35] text-[#FDFBF9] hover:bg-[#1E2B25]"}`}>
                  {isCurrent ? t.billing.current : busy === p.id ? "…" : <>{managed ? `${t.billing.switchTo} ${p.name}` : elite ? t.billing.upgrade : t.billing.subscribe} <ArrowRight size={16} /></>}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[#6B6A67] mt-4" data-testid="billing-note">{t.billing.note}</p>
      </div>

      <Receipts reloadKey={reloadKey} />
      <ConfirmPlanModal action={action} info={info} onClose={() => setAction(null)} onDone={reloadAll} />

      <Modal open={!!onvoSub} onClose={() => setOnvoSub(null)} title={t.billing.cardTitle} testid="onvo-payment-modal">
        <p className="text-xs text-[#6B6A67] mb-4 flex items-center gap-2"><Lock size={14} /> {t.billing.cardHint}</p>
        <div id="onvo-container" data-testid="onvo-card-form" className="min-h-[320px]" />
      </Modal>
    </div>
  );
}
